/**
 * Single source of truth for Firebase / GCP constants that are shared
 * across all apps in the monorepo (webapp, math4ai, functions, tests,
 * tooling). Project-specific values like `projectId` stay in each
 * app's own config — those vary per deployment target. Values here
 * are universal.
 */

/**
 * The GCP region that all Cloud Functions in this monorepo are
 * deployed to. Used to construct Cloud Functions URLs (production and
 * emulator) and to scope the Firebase JS SDK's `getFunctions(app,
 * region)` calls. If the org ever splits Cloud Functions across
 * regions, this becomes a per-function concern and the constant
 * widens to a union — until then, every consumer agrees.
 */
export const FUNCTIONS_REGION = 'us-central1';

/**
 * Production Firebase Web API key, mirrored from the webapp client
 * config so server-side callers (scheduled liveness probes, admin
 * scripts) can hit the public Identity Toolkit REST endpoints (e.g.
 * `accounts:signInWithCustomToken`) without re-hardcoding.
 *
 * Firebase Web API keys are intentionally public — they identify the
 * project to Google's REST surface and are not authentication
 * credentials. They ship in every webapp bundle.
 */
export const FIREBASE_WEB_API_KEY_PRODUCTION =
  'AIzaSyC5R06hZdnowglrzoD_RVw7EVH3nSGE0Yo';

/**
 * Connection target for a running Firebase Functions emulator.
 *
 * @param host The emulator's host (typically `127.0.0.1` for local
 *   dev). No protocol — just the host.
 * @param port The emulator's port (typically `5001`).
 */
export interface FunctionsEmulatorTarget {
  host: string;
  port: number;
}

/**
 * Returns the base URL for invoking Cloud Functions for a given
 * project, either against the production deployment or against a
 * running local Firebase Functions emulator. Mirrors the URL shape
 * the Firebase JS SDK uses internally for `httpsCallable`.
 *
 * Production: `https://{region}-{projectId}.cloudfunctions.net`
 *
 * Emulator:   `http://{host}:{port}/{projectId}/{region}`
 *
 * Use this anywhere code needs to construct a Cloud Function URL by
 * hand (raw HTTP from Pyodide, smoke tests, healthcheck dashboards,
 * etc.) so the choice of region and the URL shape stay in one place.
 *
 * @param params.projectId The Firebase / GCP project to target.
 *   Required because each app in the monorepo may target a different
 *   project (webapp → `codeinplace-production`, math4ai → its own).
 * @param params.emulator Pass an object to target a running emulator;
 *   omit (or pass `null`) to get the production base URL. Callers
 *   typically derive this from their app's emulator-services env var
 *   (e.g. `VITE_FIREBASE_EMULATOR_SERVICES` containing `'functions'`).
 * @return The base URL with no trailing slash. Append `/{functionName}`
 *   to invoke a specific function.
 */
export function getFunctionsBaseUrl(params: {
  projectId: string;
  emulator?: FunctionsEmulatorTarget | null;
}): string {
  const {projectId, emulator} = params;
  if (emulator) {
    return `http://${emulator.host}:${String(emulator.port)}/${projectId}/${FUNCTIONS_REGION}`;
  }
  return `https://${FUNCTIONS_REGION}-${projectId}.cloudfunctions.net`;
}
